import { useEffect } from "react";
import styles from "./Drawer.module.css";

/**
 * Componente Drawer reutilizable
 * Drawer lateral que se desliza desde la derecha
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el drawer está abierto
 * @param {function} props.onClose - Función para cerrar el drawer
 * @param {string} props.title - Título del drawer
 * @param {string} props.subtitle - Subtítulo opcional
 * @param {string} props.thumbnail - URL de imagen miniatura opcional
 * @param {React.ReactNode} props.children - Contenido del drawer
 * @param {React.ReactNode} props.footer - Contenido del footer (botones)
 * @param {boolean} props.darkMode - Activa el modo oscuro
 */
const Drawer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  thumbnail,
  children,
  footer,
  darkMode = false,
}) => {
  // Prevenir scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Cerrar con tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={styles["drawer-overlay"]} 
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Container */}
      <div 
        className={`${styles["drawer-container"]} ${darkMode ? styles.dark : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {/* Header */}
        <div className={styles["drawer-header"]}>
          <div className={styles["drawer-title-section"]}>
            {thumbnail && (
              <img 
                src={thumbnail} 
                alt={title}
                className={styles["drawer-thumbnail"]}
              />
            )}
            <div className={styles["drawer-title-text"]}>
              <h2 id="drawer-title" className={styles["drawer-title"]}>
                {title}
              </h2>
              {subtitle && (
                <p className={styles["drawer-subtitle"]}>{subtitle}</p>
              )}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className={styles["drawer-close-button"]}
            aria-label="Cerrar"
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className={styles["drawer-content"]}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={styles["drawer-footer"]}>
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default Drawer;
